import { fetchCache } from './utils/fetchCache.js';
import { WORKER_URL } from './config.js';
import { storage } from './utils/storage.js';
import { normalizeSiteLanguageCode } from './utils/languageData.js';

const translations = {};
let currentLanguage = 'en';

/** @deprecated Prefer normalizeSiteLanguageCode from languageData.js for UI locale */
const normalizeLang = (lang) => normalizeSiteLanguageCode(lang);

function detectBrowserLanguage() {
  try {
    // Check navigator.language and navigator.languages
    const lang = navigator.language || navigator.languages?.[0] || 'en';
    const langCode = lang.split('-')[0]; // Get primary language code (e.g., 'en' from 'en-US')

    const languageMap = {
      'ja': 'ja',
      'zh': 'zh-CN',
      'ko': 'ko',
      'en': 'en',
      'es': 'es',
      'fr': 'fr',
      'pt': 'pt-BR',
      'ru': 'ru',
      'hi': 'hi',
      'ar': 'en'
    };

    return languageMap[langCode] || 'en';
  } catch {
    return 'en';
  }
}

async function loadTranslationFile(type, lang, page = null) {
  const path = `../translations/${page ? `${page}-${lang}.json` : `nav-${lang}.json`}`;
  try {
    const res = await fetchCache.fetchJSON(path);
    if (!res || typeof res !== 'object') throw new Error(`Translation not found: ${path}`);
    return res;
  } catch (error) {
    // Silently fall back to English if translation file doesn't exist
    if (lang !== 'en') {
      const fallbackPath = `../translations/${page ? `${page}-en.json` : `nav-en.json`}`;
      try {
        const fallback = await fetchCache.fetchJSON(fallbackPath);
        return (fallback && typeof fallback === 'object') ? fallback : {};
      } catch (fallbackError) {
        console.warn(`Translation file not found: ${path}, and fallback failed: ${fallbackPath}`);
        return {};
      }
    }
    console.warn(`Translation file not found: ${path}`);
    return {};
  }
}

async function loadNavTranslations(lang = 'en') {
  lang = normalizeLang(lang);
  
  translations[lang] = translations[lang] || {};
  const raw = await loadTranslationFile('nav', lang);
  const safe = (raw && typeof raw === 'object') ? raw : {};

  // Normalize to flat keys: both "nav.home" and "home" formats map to "home"
  translations[lang].nav = {};
  for (const [k, v] of Object.entries(safe)) {
    const flatKey = k.startsWith('nav.') ? k.slice(4) : k;
    translations[lang].nav[flatKey] = v;
  }
  
  // If we got an empty object, try to use English fallback
  if (Object.keys(translations[lang].nav).length === 0 && translations.en?.nav) {
    translations[lang].nav = translations.en.nav;
  }
}

async function loadOnePatchNotesFile(lang) {
  lang = normalizeLang(lang);
  translations[lang] = translations[lang] || {};
  const raw = await loadTranslationFile('page', lang, 'patch-notes');
  const safe = (raw && typeof raw === 'object') ? raw : {};
  translations[lang].patchNotes = (safe.patch && typeof safe.patch === 'object') ? safe.patch : {};
}

// Load patch-notes-{lang}.json for the updates page. English is always loaded as
// well so individual sections/items can fall back when a locale is incomplete.
async function loadPatchNotesTranslations(lang = 'en') {
  lang = normalizeLang(lang);
  await loadOnePatchNotesFile(lang);
  if (lang !== 'en') {
    await loadOnePatchNotesFile('en');
  }
}

// Resolve a single patch-notes section to translated title + items, falling back
// to English per item when the active locale is incomplete.
function resolvePatchNoteSection(slug, sectionId, lang) {
  lang = normalizeLang(lang);
  const localized = translations[lang]?.patchNotes?.[slug]?.[sectionId];
  const english = translations.en?.patchNotes?.[slug]?.[sectionId];
  if (!localized && !english) return null;

  const englishItems = Array.isArray(english?.items) ? english.items : [];
  const localizedItems = Array.isArray(localized?.items) ? localized.items : [];
  const count = Math.max(englishItems.length, localizedItems.length);

  const items = [];
  for (let index = 0; index < count; index++) {
    const item = localizedItems[index] ?? englishItems[index];
    if (item) items.push(item);
  }

  const mediaLegends = (english?.mediaLegends ?? []).map((group, groupIndex) =>
    group.map((legend, imageIndex) => localized?.mediaLegends?.[groupIndex]?.[imageIndex] ?? legend)
  );

  return {
    title: localized?.title ?? english?.title ?? '',
    items,
    mediaLegends,
  };
}

const CREATE_PAGES_WITH_COMMON = ['create-character', 'create-scenario', 'create-video'];
const RECRUIT_LORA_KEYS = ['lora_section_title', 'lora_id_placeholder', 'lora_strength_placeholder', 'lora_note'];

const pickTranslationKeys = (source, keys) => {
  const picked = {};
  if (!source || typeof source !== 'object') return picked;
  keys.forEach((key) => {
    if (source[key] != null && source[key] !== '') picked[key] = source[key];
  });
  return picked;
};

async function loadTranslations(page, lang = 'en') {
  lang = normalizeLang(lang);
  
  translations[lang] = translations[lang] || {};

  if (page === 'create-character') {
    const [commonData, recruitData] = await Promise.all([
      loadTranslationFile('page', lang, 'create-common'),
      loadTranslationFile('page', lang, 'recruit-character'),
    ]);
    const safeCommon = (commonData && typeof commonData === 'object') ? commonData : {};
    const safeRecruit = (recruitData && typeof recruitData === 'object') ? recruitData : {};
    translations[lang].createCommon = safeCommon;
    translations[lang][page] = { ...safeCommon, ...safeRecruit };
  } else if (page === 'create-video') {
    const [commonData, pageData, recruitData] = await Promise.all([
      loadTranslationFile('page', lang, 'create-common'),
      loadTranslationFile('page', lang, page),
      loadTranslationFile('page', lang, 'recruit-character'),
    ]);
    const safeCommon = (commonData && typeof commonData === 'object') ? commonData : {};
    const safePage = (pageData && typeof pageData === 'object') ? pageData : {};
    const safeRecruit = (recruitData && typeof recruitData === 'object') ? recruitData : {};
    translations[lang].createCommon = safeCommon;
    translations[lang][page] = {
      ...safeCommon,
      ...safePage,
      ...pickTranslationKeys(safeRecruit, RECRUIT_LORA_KEYS),
    };
  } else if (CREATE_PAGES_WITH_COMMON.includes(page)) {
    const [commonData, pageData] = await Promise.all([
      loadTranslationFile('page', lang, 'create-common'),
      loadTranslationFile('page', lang, page),
    ]);
    const safeCommon = (commonData && typeof commonData === 'object') ? commonData : {};
    const safePage = (pageData && typeof pageData === 'object') ? pageData : {};
    translations[lang].createCommon = safeCommon;
    translations[lang][page] = { ...safeCommon, ...safePage };
  } else {
    const data = await loadTranslationFile('page', lang, page);
    translations[lang][page] = (data && typeof data === 'object') ? data : {};
  }
  
  // If we got an empty object, try to use English fallback
  if (Object.keys(translations[lang][page]).length === 0 && translations.en?.[page]) {
    translations[lang][page] = translations.en[page];
  }
}

// Resolve values from nested objects using dotted keys (e.g., "character_modal.age")
function getByPath(obj, key) {
  if (!obj || !key) return undefined;
  if (!key.includes('.')) return obj[key];
  return key.split('.').reduce((acc, part) => (acc && acc[part] !== undefined) ? acc[part] : undefined, obj);
}

/**
 * Replace all `{key}` placeholders in a string with values from a vars map.
 * This is the single canonical interpolation helper for the whole app.
 * @param {string} str
 * @param {Record<string, string|number>} vars
 * @returns {string}
 */
const interpolate = (str, vars) =>
  Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, String(v)), str);

const getGender = () => document.getElementById('ai-character-gender')?.value || 'female';

function applyTranslations(page, lang) {
  lang = normalizeLang(lang);
  const t = translations[lang]?.[page] || translations.en?.[page] || {};
  const nav = translations[lang]?.nav || translations.en?.nav || {};
  const missing = [];

  const enT = translations.en?.[page] || {};
  const resolve = (key) => {
    return (
      getByPath(t, key) ??
      (t && t[key]) ??
      getByPath(enT, key) ??
      (enT && enT[key])
    );
  };
  const resolveWithGender = (key) => {
    const genderKey = key === 'appearance_description'
      ? 'appearance_prompt_' + getGender()
      : `${key}_${getGender()}`;
    return resolve(genderKey) ?? resolve(key);
  };

  document.querySelectorAll('[data-translate]').forEach(el => {
    const key = el.getAttribute('data-translate');
    const isNavKey = key.startsWith('nav.');
    const isGenderKey = el.hasAttribute('data-translate-gender');
    const value = isNavKey
      ? (nav[key.replace('nav.', '')] ?? translations.en?.nav?.[key.replace('nav.', '')])
      : isGenderKey ? resolveWithGender(key) : resolve(key);
    
    if (value) {
      const n = el.getAttribute('data-n');
      el.innerHTML = n !== null ? interpolate(String(value), { n }) : value;
    } else {
      // Only warn if the key is relevant to the current context
      const isCurrentPageKey = key.startsWith(`${page}.`);
      const hasNoPrefix = !key.includes('.');
      if ((page === 'nav' && isNavKey) || (page !== 'nav' && (isCurrentPageKey || hasNoPrefix || isNavKey))) {
        missing.push(key);
      }
    }
  });

  document.querySelectorAll('[data-translate-placeholder]').forEach(el => {
    const key = el.getAttribute('data-translate-placeholder');
    const value = resolve(key);
    if (value) {
      /** @type {HTMLElement} */
      const element = el;
      element.placeholder = value;
    }
  });

  document.querySelectorAll('[data-translate-attr]').forEach(el => {
    const attrSpec = el.getAttribute('data-translate-attr');
    if (attrSpec.includes(':')) {
      const [attr, key] = attrSpec.split(':');
      const value = resolve(key);
      if (value) el.setAttribute(attr, value);
    }
  });

  const titleEl = document.querySelector('title[data-translate]');
  if (titleEl && t[titleEl.getAttribute('data-translate')]) {
    document.title = t[titleEl.getAttribute('data-translate')];
  }

  if (missing.length) console.warn(`Missing ${lang}: ${missing.join(', ')}`);
  document.dispatchEvent(new CustomEvent('translationsApplied', {
    detail: { page, language: lang },
  }));
}

function applyTranslationsToContainer(container, page, lang) {
  lang = normalizeLang(lang);
  const t = translations[lang]?.[page] || translations.en?.[page] || {};
  const nav = translations[lang]?.nav || translations.en?.nav || {};
  const missing = [];

  const enT = translations.en?.[page] || {};
  const resolve = (key) => {
    return (
      getByPath(t, key) ??
      (t && t[key]) ??
      getByPath(enT, key) ??
      (enT && enT[key])
    );
  };
  const resolveWithGender = (key) => {
    const genderKey = key === 'appearance_description'
      ? 'appearance_prompt_' + getGender()
      : `${key}_${getGender()}`;
    return resolve(genderKey) ?? resolve(key);
  };

  container.querySelectorAll('[data-translate]').forEach(el => {
    const key = el.getAttribute('data-translate');
    const isNavKey = key.startsWith('nav.');
    const isGenderKey = el.hasAttribute('data-translate-gender');
    const value = isNavKey
      ? (nav[key.replace('nav.', '')] ?? translations.en?.nav?.[key.replace('nav.', '')])
      : isGenderKey ? resolveWithGender(key) : resolve(key);
    
    if (value) {
      const n = el.getAttribute('data-n');
      el.innerHTML = n !== null ? interpolate(String(value), { n }) : value;
    } else {
      // Only warn if the key is relevant to the current context
      const isCurrentPageKey = key.startsWith(`${page}.`);
      const hasNoPrefix = !key.includes('.');
      if ((page === 'nav' && isNavKey) || (page !== 'nav' && (isCurrentPageKey || hasNoPrefix || isNavKey))) {
        missing.push(key);
      }
    }
  });

  container.querySelectorAll('[data-translate-placeholder]').forEach(el => {
    const key = el.getAttribute('data-translate-placeholder');
    const value = resolve(key);
    if (value) {
      el.placeholder = value;
    }
  });

  container.querySelectorAll('[data-translate-attr]').forEach(el => {
    const attrSpec = el.getAttribute('data-translate-attr');
    if (attrSpec.includes(':')) {
      const [attr, key] = attrSpec.split(':');
      const value = resolve(key);
      if (value) el.setAttribute(attr, value);
    }
  });

  const titleEl = container.querySelector('title[data-translate]');
  if (titleEl && t[titleEl.getAttribute('data-translate')]) {
    document.title = t[titleEl.getAttribute('data-translate')];
  }

  if (missing.length) console.warn(`Missing ${lang}: ${missing.join(', ')}`);
}

async function initializeTranslations(page, lang = null) {
  currentLanguage = normalizeLang(lang ||
    storage.get('displayLanguage') ||
    await detectBrowserLanguage());

  storage.set('displayLanguage', currentLanguage);

  const loadTasks = [
    loadNavTranslations(currentLanguage),
    loadTranslations(page, currentLanguage),
    loadCreateCommonTranslations(currentLanguage),
  ];
  if (page === 'updates') {
    loadTasks.push(loadPatchNotesTranslations(currentLanguage));
  }
  await Promise.all(loadTasks);

  applyTranslations(page, currentLanguage);

  if (!window.translationEventListenerAdded) {
    window.translationEventListenerAdded = true;
    document.addEventListener('languageChanged', (e) => {
      const newLang = normalizeLang(e.detail?.language ||
        storage.get('displayLanguage'));
      switchLanguage(page, newLang);
    });
  }
}

async function switchLanguage(page, newLang) {
  newLang = normalizeLang(newLang);
  currentLanguage = newLang;
  
  storage.set('displayLanguage', newLang);
  
  document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  
  translations[newLang] = {};
  
  const loadTasks = [
    loadNavTranslations(newLang),
    loadTranslations(page, newLang),
    loadCreateCommonTranslations(newLang),
  ];
  if (page === 'updates') {
    loadTasks.push(loadPatchNotesTranslations(newLang));
  }
  await Promise.all(loadTasks);
  
  applyTranslations(page, newLang);
  
  document.dispatchEvent(new CustomEvent('languageSwitched', {
    detail: { language: newLang, page }
  }));
}

const getCurrentLanguage = () => currentLanguage;

function applyGenderTranslations(page, lang) {
  lang = normalizeLang(lang || currentLanguage);
  const t = translations[lang]?.[page] || translations.en?.[page] || {};
  const enT = translations.en?.[page] || {};
  const gender = getGender();

  const resolve = (key) => {
    return (
      getByPath(t, key) ??
      (t && t[key]) ??
      getByPath(enT, key) ??
      (enT && enT[key])
    );
  };
  const resolveWithGender = (key) => {
    const genderKey = key === 'appearance_description'
      ? 'appearance_prompt_' + gender
      : `${key}_${gender}`;
    return resolve(genderKey) ?? resolve(key);
  };

  document.querySelectorAll('[data-translate][data-translate-gender]').forEach(el => {
    const key = el.getAttribute('data-translate');
    const value = resolveWithGender(key);
    if (value) el.innerHTML = value;
  });

  document.querySelectorAll('[data-translate-placeholder][data-translate-gender]').forEach(el => {
    const key = el.getAttribute('data-translate-placeholder');
    const value = resolveWithGender(key);
    if (value) el.placeholder = value;
  });
}

async function ensureNavTranslationsLoaded(lang) {
  lang = normalizeLang(lang);
  if (translations[lang]?.nav && Object.keys(translations[lang].nav).length > 0) return;
  await loadNavTranslations(lang);
}

async function loadCreateCommonTranslations(lang = 'en') {
  lang = normalizeLang(lang);
  translations[lang] = translations[lang] || {};
  const data = await loadTranslationFile('page', lang, 'create-common');
  translations[lang].createCommon = (data && typeof data === 'object') ? data : {};
  if (Object.keys(translations[lang].createCommon).length === 0 && translations.en?.createCommon) {
    translations[lang].createCommon = translations.en.createCommon;
  }
}

/**
 * Resolve a dotted key from create-common translations with English fallback.
 * @param {string} key
 * @param {Record<string, string|number>} [vars]
 * @param {string} [lang]
 * @returns {string}
 */
function resolveCreateCommonText(key, vars = null, lang = null) {
  lang = normalizeLang(lang || currentLanguage || 'en');
  const t = translations[lang]?.createCommon || {};
  const enT = translations.en?.createCommon || {};
  const raw = getByPath(t, key) ?? getByPath(enT, key) ?? '';
  if (!raw) return '';
  if (vars && Object.keys(vars).length) return interpolate(String(raw), vars);
  return String(raw);
}

/**
 * Resolve an image-generation API error code to a translated message.
 * Used when the worker returns error codes (e.g. prompt_rejected_child_explicit) in response.details.
 * @param {string} code - Error code from API (e.g. details or error field)
 * @returns {string} Translated message or the code if no translation exists
 */
function resolveImageGenError(code) {
  if (!code || typeof code !== 'string') return code || '';
  const path = (typeof window !== 'undefined' && window.location?.pathname) || '';
  const page = path.includes('create-scenario') ? 'create-scenario' : path.includes('create-video') ? 'create-video' : 'create-character';
  const lang = normalizeLang(currentLanguage || 'en');
  const t = translations[lang]?.[page] || translations.en?.[page];
  const commonT = translations[lang]?.createCommon || translations.en?.createCommon;
  const msg = getByPath(t, 'errors.' + code) ?? getByPath(commonT, 'errors.' + code);
  return msg || code;
}

export { initializeTranslations, switchLanguage, getCurrentLanguage, applyTranslations, loadTranslations, normalizeLang, applyTranslationsToContainer, applyGenderTranslations, interpolate, ensureNavTranslationsLoaded, getByPath, resolveImageGenError, loadPatchNotesTranslations, resolvePatchNoteSection, loadCreateCommonTranslations, resolveCreateCommonText };

window.applyTranslations = applyTranslations;
window.applyGenderTranslations = applyGenderTranslations;
window.getCurrentLanguage = getCurrentLanguage;
window.loadTranslations = loadTranslations;
window.interpolate = interpolate;
// Expose the translations cache so that dynamically loaded modules can directly access
// already-fetched translations without re-importing this file. Some legacy code (e.g.
// settings-modal.js) expects `window.translations` to exist. Keeping a live reference
// ensures that updates made via loadTranslations()/loadNavTranslations() are visible
// everywhere.
window.translations = translations;

document.addEventListener('DOMContentLoaded', () => {
  let page = window.location.pathname.split('/').pop().replace('.html', '');
  if (!page || page === 'index') page = 'rankings';
  initializeTranslations(page);
});
